import {
  Box,
  Button,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import {
  ENTERPRISE_IMAGE_PATH,
  TEXT_COLOR,
  THEME,
  isEnterprise,
} from "../../theme";
import logo from "../../components/ResponsiveNavbar/logo.png";
import { userDetailsStageStyles } from "./styles";
import { UserDetailsStageProps } from "./types";
import { styles } from "../../pages/InitialSettings/styles";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { CustomCircularProgress } from "../ProgressComponents/CustomCircularProgress";
import { useState } from "react";

export const UserDetailsStage: React.FC<UserDetailsStageProps> = ({
  stageName,
  firstName,
  lastName,
  setStage,
  isStageNameEmpty,
  isStageNameError,
  isFirstNameError,
  isLastNameError,
  duplicateUsername,
  handleUsernameChange,
  handleChangeFirstName,
  handleChangeLastName,
  checkValidation,
  getUserResponse,
  email,
  isLoading,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  return (
    <>
      <Box sx={userDetailsStageStyles.mainContainer}>
        <Box sx={userDetailsStageStyles.childOfMainComponent}>
          <Box
            sx={{
              ...styles.groupAddOutlinedIconContainer,
              color: TEXT_COLOR,
              borderColor: TEXT_COLOR,
            }}
          >
            <img
              src="/assets/icons/InitialSettingsIcons/user-01.svg"
              style={{
                ...styles.groupAddOutlinedIcon,
                filter: THEME === "dark" ? "invert(0%)" : "invert(100%)",
              }}
              alt="icon"
            />
          </Box>
          <img
            style={userDetailsStageStyles.logoImage}
            className="logo-image"
            src={isEnterprise ? ENTERPRISE_IMAGE_PATH : logo}
            alt="logo"
          />
          <Box sx={userDetailsStageStyles.textFieldContainer}>
            <Typography sx={{ fontSize: "18px" }}>
              Username / Stage Name <span className="asterisk">*</span>
            </Typography>
            <TextField
              disabled={
                getUserResponse?.getUsername() !== undefined &&
                getUserResponse.getUsername() !== "" &&
                !getUserResponse.getUsername().includes("dummy_ommuse")
              }
              error={isStageNameEmpty || isStageNameError || duplicateUsername}
              value={stageName.includes("dummy_ommuse") ? "" : stageName}
              placeholder="Enter username"
              fullWidth
              sx={userDetailsStageStyles.textField}
              onChange={(e) => handleUsernameChange(e)}
              InputProps={{
                endAdornment:
                  isStageNameEmpty || isStageNameError || duplicateUsername ? (
                    <InputAdornment position="end">
                      <Tooltip
                        placement="top"
                        title={
                          isStageNameEmpty
                            ? "Please enter Username / Stage Name "
                            : duplicateUsername
                            ? "Username / Stage Name already exists"
                            : "Special characters, and spaces are not allowed"
                        }
                      >
                        <ErrorOutlineOutlinedIcon
                          sx={{ cursor: "pointer" }}
                          color="error"
                        />
                      </Tooltip>
                    </InputAdornment>
                  ) : (
                    <></>
                  ),
              }}
            />
            <Typography sx={{ fontSize: "16px", marginTop: "4px" }}>
              Note: Once set, stage / user name cannot be changed
            </Typography>
          </Box>
          <Box sx={userDetailsStageStyles.textFieldContainer}>
            <Typography sx={{ fontSize: "18px" }}>Email</Typography>
            <TextField
              disabled
              error={isFirstNameError}
              value={email ?? ""}
              fullWidth
              sx={userDetailsStageStyles.textField}
            />
          </Box>
          <Box sx={userDetailsStageStyles.textFieldContainer}>
            <Typography sx={{ fontSize: "18px" }}>First Name</Typography>
            <TextField
              error={isFirstNameError}
              value={firstName}
              placeholder="Enter first name"
              fullWidth
              sx={userDetailsStageStyles.textField}
              onChange={(e) => handleChangeFirstName(e)}
              InputProps={{
                endAdornment: isFirstNameError ? (
                  <InputAdornment position="end">
                    <Tooltip
                      placement="top"
                      title={
                        isFirstNameError
                          ? "Special characters,and spaces are not allowed"
                          : "Please enter first name"
                      }
                    >
                      <ErrorOutlineOutlinedIcon
                        sx={{ cursor: "pointer" }}
                        color="error"
                      />
                    </Tooltip>
                  </InputAdornment>
                ) : (
                  <></>
                ),
              }}
            />
          </Box>
          <Box sx={userDetailsStageStyles.textFieldContainer}>
            <Typography sx={{ fontSize: "18px" }}>Last Name</Typography>
            <TextField
              error={isLastNameError}
              value={lastName}
              placeholder="Enter last name"
              fullWidth
              sx={userDetailsStageStyles.textField}
              onChange={(e) => handleChangeLastName(e)}
              InputProps={{
                endAdornment: isLastNameError ? (
                  <InputAdornment position="end">
                    <Tooltip
                      placement="top"
                      title={
                        isLastNameError
                          ? "Special characters, and spaces are not allowed"
                          : "Please enter last name"
                      }
                    >
                      <ErrorOutlineOutlinedIcon
                        sx={{ cursor: "pointer" }}
                        color="error"
                      />
                    </Tooltip>
                  </InputAdornment>
                ) : (
                  <></>
                ),
              }}
            />
          </Box>

          <FormGroup sx={userDetailsStageStyles.formGroup}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  required
                  checked={isChecked}
                  onChange={() => {
                    setIsChecked((prev) => !prev);
                  }}
                />
              }
              label={
                <>
                  <Typography color="error" component="span">
                    *
                  </Typography>
                  {" I agree to the "}
                  <Link href="https://ommuse.com/terms-of-use" target="_blank">
                    Terms and Conditions
                  </Link>
                  {" and "}
                  <Link
                    href="https://ommuse.com/privacy-policy"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                  .
                </>
              }
            />
          </FormGroup>

          <Box
            sx={{
              width: "110%",
              marginTop: "15px",
            }}
          >
            <Button
              disabled={isLoading || !isChecked}
              onClick={() => {
                const isFirstStep = true;
                checkValidation(isFirstStep);
              }}
              sx={userDetailsStageStyles.button}
              fullWidth
            >
              {isLoading ? (
                <CustomCircularProgress size={22} customcolor="white" />
              ) : (
                "Next"
              )}
            </Button>
          </Box>
          <Box sx={userDetailsStageStyles.indicatorContainer}>
            <Box
              onClick={() => setStage(0)}
              sx={{
                ...userDetailsStageStyles.indicatorDot,
                backgroundColor: TEXT_COLOR,
              }}
            ></Box>
            <Box
              onClick={() => setStage(1)}
              sx={userDetailsStageStyles.indicatorDot}
            ></Box>
            <Box
              onClick={() => setStage(2)}
              sx={userDetailsStageStyles.indicatorDot}
            ></Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
